import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import {
  useGetApiCsvSchemasId,
  usePostApiCsvRecordsFilter,
} from '@/api/gen/api';
import GenerarFiltros from './GenerarFiltros';
import CsvSchemaTable from './CsvSchemaTable';

export default function CsvSchemasData() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutate: postApiCsvRecordsFilter } = usePostApiCsvRecordsFilter();
  const hasLoadedInitialData = useRef(false);
  const scrollPositionRef = useRef(0);

  const [csvRecords, setCsvRecords] = useState<any[]>([]);
  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [pagination, setPagination] = useState({
    totalCount: 0,
    page: 1,
    pageSize: 10,
  });
  const [currentFilterRequest, setCurrentFilterRequest] = useState<any>(null);

  const {
    data: csvSchema,
    isLoading,
    error,
  } = useGetApiCsvSchemasId(Number(id));

  // Guardar y restaurar la posición del scroll
  useEffect(() => {
    const saveScrollPosition = () => {
      scrollPositionRef.current = window.scrollY;
    };

    window.addEventListener('scroll', saveScrollPosition);
    return () => window.removeEventListener('scroll', saveScrollPosition);
  }, []);

  // Restaurar posición del scroll después de actualizar los datos
  useEffect(() => {
    if (!isLoadingRecords && scrollPositionRef.current > 0) {
      window.scrollTo(0, scrollPositionRef.current);
    }
  }, [csvRecords, isLoadingRecords]);

  // Cargar registros por defecto solo una vez al inicio
  useEffect(() => {
    if (csvSchema && csvSchema.id && !hasLoadedInitialData.current) {
      hasLoadedInitialData.current = true;
      loadDefaultRecords();
    }
  }, [csvSchema]);

  const loadDefaultRecords = useCallback(() => {
    setIsLoadingRecords(true);
    postApiCsvRecordsFilter(
      {
        data: {
          csvSchemaId: Number(id),
          filters: [],
          orderByDescending: true,
        },
        params: {
          page: 1,
          pageSize: 10,
        },
      },
      {
        onSuccess: response => {
          setCsvRecords((response as any)?.items || []);
          setPagination({
            totalCount: (response as any)?.totalCount || 0,
            page: (response as any)?.page || 1,
            pageSize: (response as any)?.pageSize || 10,
          });
          setIsLoadingRecords(false);
        },
        onError: err => {
          console.error('Error al cargar registros:', err);
          setIsLoadingRecords(false);
        },
      }
    );
  }, [id, postApiCsvRecordsFilter]);

  const handleApplyFilters = useCallback(
    (filterRequest: any, page = 1, pageSize = 10) => {
      setIsLoadingRecords(true);
      // Guardar el filtro actual para poder reutilizarlo en paginación
      setCurrentFilterRequest(filterRequest);

      postApiCsvRecordsFilter(
        {
          data: filterRequest,
          params: {
            page: page,
            pageSize: pageSize,
          },
        },
        {
          onSuccess: response => {
            setCsvRecords((response as any)?.items || []);
            setPagination({
              totalCount: (response as any)?.totalCount || 0,
              page: (response as any)?.page || page,
              pageSize: (response as any)?.pageSize || pageSize,
            });
            setIsLoadingRecords(false);
          },
          onError: err => {
            console.error('Error al aplicar filtros:', err);
            setIsLoadingRecords(false);
          },
        }
      );
    },
    [postApiCsvRecordsFilter]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      // Usar los filtros actuales guardados, o usar filtros por defecto
      const filters = currentFilterRequest || {
        csvSchemaId: Number(id),
        filters: [],
        orderByDescending: true,
      };
      handleApplyFilters(filters, newPage, pagination.pageSize);
    },
    [currentFilterRequest, id, pagination.pageSize, handleApplyFilters]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      // Usar los filtros actuales guardados, o usar filtros por defecto
      const filters = currentFilterRequest || {
        csvSchemaId: Number(id),
        filters: [],
        orderByDescending: true,
      };
      handleApplyFilters(filters, 1, newPageSize);
    },
    [currentFilterRequest, id, handleApplyFilters]
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !csvSchema) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity='error'>Error al cargar el esquema CSV</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        gap: 2,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          position: 'sticky',
          top: 0,
          bgcolor: 'background.default',
          zIndex: 1,
          py: 1,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/CsvSchemas')}
          variant='outlined'
        >
          Volver
        </Button>
        <Typography variant='h4' component='h1'>
          {csvSchema.name}
        </Typography>
      </Box>

      <GenerarFiltros
        csvSchema={csvSchema}
        onApplyFilters={filterRequest =>
          handleApplyFilters(filterRequest, 1, pagination.pageSize)
        }
        isLoading={isLoadingRecords}
      />

      <CsvSchemaTable
        csvSchemaHeader={csvSchema}
        data={csvRecords}
        isLoading={isLoadingRecords}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />
    </Box>
  );
}
